import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Card, Typography, useMediaQueries } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { useStoreActions } from '~/store/store-context';
import { BrandStep } from './components/BrandStep/BrandStep';
import { ClosingStep } from './components/ClosingStep/ClosingStep';
import { OnboardingLayout } from './components/OnboardingLayout/OnboardingLayout';
import { BusinessDetailsAStep } from './components/steps/BusinessDetailsAStep/BusinessDetailsAStep';
import { BusinessDetailsBStep } from './components/steps/BusinessDetailsBStep/BusinessDetailsBStep';
import { IntegrationsStep } from './components/steps/IntegrationsStep/IntegrationsStep';
import { IntentStep } from './components/steps/IntentStep/IntentStep';
import { IntroStep } from './components/steps/IntroStep/IntroStep';
import { WaitingStep } from './components/WaitingStep/WaitingStep';
import { useOnboardingFlow } from './hooks/useOnboardingFlow';
import styles from './VendorOnboarding.module.scss';
const EMPTY_POSTAL_ADDRESS = {
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
};
export const GetVendorOnboardingTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const VendorOnboarding = (param)=>{
    let { testId, onComplete } = param;
    var _useStore;
    const testIds = useMemo(()=>GetVendorOnboardingTID(testId), [
        testId
    ]);
    const { t: aTexts } = useLocaleKeys('vendorOnboarding.businessDetailsAStep');
    const { t: bTexts } = useLocaleKeys('vendorOnboarding.businessDetailsBStep');
    const { t: intentTexts } = useLocaleKeys('vendorOnboarding.intentStep');
    const { t: integrationsTexts } = useLocaleKeys('vendorOnboarding.integrationsStep');
    const [businessDetailsA, setBusinessDetailsA] = useState({
        companyName: '',
        postalAddress: EMPTY_POSTAL_ADDRESS,
        phoneNumber: ''
    });
    const [businessDetailsB, setBusinessDetailsB] = useState({
        businessDescription: '',
        numInvoices: null,
        teamSize: null
    });
    const [intent, setIntent] = useState({
        purposes: []
    });
    const [integrations, setIntegrations] = useState({
        selectedTools: [],
        otherTools: []
    });
    const skipInviteTeam = businessDetailsB.teamSize === 'just_me';
    const skipQboXero = !integrations.selectedTools.includes('toolQuickbooksOnline') && !integrations.selectedTools.includes('toolXero');
    const { currentStep, progress, isSkippableStep, isBackDisabled, goNext, goBack } = useOnboardingFlow({
        skipInviteTeam,
        skipQboXero
    });
    const { mobile: isMobile } = useMediaQueries();
    const worksAt = (_useStore = useStore(selectors.user.business)) !== null && _useStore !== void 0 ? _useStore : null;
    const brandStepRef = useRef(null);
    const { logBI } = useStoreActions();
    const submitRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasCreatedBusiness, setHasCreatedBusiness] = useState(false);
    const [lastSubmittedBusinessSignature, setLastSubmittedBusinessSignature] = useState(null);
    const [creditsAwarded, setCreditsAwarded] = useState(false);
    const [direction, setDirection] = useState('forward');
    const isWaitingOrClosing = currentStep === 'waiting' || currentStep === 'closing';
    useEffect(()=>{
        setIsSubmitting(false);
    }, [
        currentStep
    ]);
    const goForward = useCallback(()=>{
        setDirection('forward');
        setTimeout(()=>goNext(), 0);
    }, [
        goNext
    ]);
    const goBackward = useCallback(()=>{
        setDirection('back');
        setTimeout(()=>goBack(), 0);
    }, [
        goBack
    ]);
    const stepConfig = useMemo(()=>({
            businessDetailsA: {
                title: aTexts.title(),
                subtitle: aTexts.subtitle()
            },
            businessDetailsB: {
                title: bTexts.title(),
                subtitle: bTexts.subtitle()
            },
            brand: {},
            intent: {
                title: intentTexts.title(),
                subtitle: intentTexts.subtitle()
            },
            integrations: {
                title: integrationsTexts.title(),
                subtitle: integrationsTexts.subtitle()
            }
        }), [
        aTexts,
        bTexts,
        intentTexts,
        integrationsTexts
    ]);
    const handleIntroComplete = ()=>{
        setCreditsAwarded(true);
        goForward();
    };
    const handleLayoutContinue = ()=>{
        if (currentStep === 'brand') {
            void (async ()=>{
                if (brandStepRef.current) {
                    const saved = await brandStepRef.current.save();
                    if (!saved) return;
                }
                goForward();
            })();
            return;
        }
        const handle = submitRef.current;
        if (handle != null) {
            void handle.submit();
            return;
        }
        goForward();
    };
    const handleLayoutSkip = ()=>{
        // TODO(ANC-13381): replace with real BI event id once assigned
        logBI(99999, {
            step: currentStep,
            action: 'skip'
        });
        goForward();
    };
    const renderStepContent = ()=>{
        if (currentStep === 'businessDetailsA') {
            return /*#__PURE__*/ _jsx(BusinessDetailsAStep, {
                values: businessDetailsA,
                onChange: setBusinessDetailsA,
                onContinue: goForward,
                submitRef: submitRef
            });
        }
        if (currentStep === 'businessDetailsB') {
            return /*#__PURE__*/ _jsx(BusinessDetailsBStep, {
                valuesFromStepA: businessDetailsA,
                values: businessDetailsB,
                onChange: setBusinessDetailsB,
                onContinue: goForward,
                submitRef: submitRef,
                onSubmittingChange: setIsSubmitting,
                hasCreatedBusiness: hasCreatedBusiness,
                onBusinessCreated: ()=>setHasCreatedBusiness(true),
                lastSubmittedSignature: lastSubmittedBusinessSignature,
                onSubmitted: setLastSubmittedBusinessSignature
            });
        }
        if (currentStep === 'intent') {
            return /*#__PURE__*/ _jsx(IntentStep, {
                values: intent,
                onChange: setIntent,
                onContinue: goForward,
                submitRef: submitRef
            });
        }
        if (currentStep === 'integrations') {
            return /*#__PURE__*/ _jsx(IntegrationsStep, {
                values: integrations,
                onChange: setIntegrations,
                onContinue: goForward,
                submitRef: submitRef
            });
        }
        if (currentStep === 'brand') {
            return /*#__PURE__*/ _jsx(BrandStep, {
                ref: brandStepRef,
                worksAt: worksAt
            });
        }
        // TODO(ANC-12230): inviteTeam / businessWebsite / brand / colors / qboXeroIntegration / waiting / closing land in later checkpoints; render nothing until then.
        return null;
    };
    const config = stepConfig[currentStep];
    return /*#__PURE__*/ _jsx(Box, {
        className: clsx(styles.root, {
            [styles.mobile]: isMobile,
            [styles.dashboardBackground]: isWaitingOrClosing
        }),
        testId: testIds.VendorOnboardingTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Card, {
            className: styles.card,
            borderSkin: "default",
            borderRadius: "m",
            children: [
                currentStep === 'intro' && /*#__PURE__*/ _jsx(IntroStep, {
                    onSkip: handleIntroComplete,
                    onComplete: handleIntroComplete,
                    creditsAlreadyAwarded: creditsAwarded
                }),
                currentStep === 'waiting' && /*#__PURE__*/ _jsx(WaitingStep, {
                    onComplete: goNext
                }),
                currentStep === 'closing' && /*#__PURE__*/ _jsx(ClosingStep, {
                    onComplete: onComplete
                }),
                currentStep !== 'intro' && currentStep !== 'waiting' && currentStep !== 'closing' && /*#__PURE__*/ _jsx(OnboardingLayout, {
                    progress: progress,
                    onBack: goBackward,
                    onContinue: handleLayoutContinue,
                    onSkip: handleLayoutSkip,
                    showSkipButton: isSkippableStep,
                    isContinueDisabled: isSubmitting,
                    isBackDisabled: isBackDisabled,
                    children: /*#__PURE__*/ _jsx(AnimatePresence, {
                        mode: "wait",
                        custom: direction,
                        initial: false,
                        children: /*#__PURE__*/ _jsxs(motion.div, {
                            custom: direction,
                            initial: (dir)=>({
                                    x: dir === 'forward' ? 50 : -50,
                                    opacity: 0
                                }),
                            animate: {
                                x: 0,
                                opacity: 1
                            },
                            exit: (dir)=>({
                                    x: dir === 'forward' ? -50 : 50,
                                    opacity: 0
                                }),
                            transition: {
                                duration: 0.4,
                                ease: 'easeInOut'
                            },
                            className: clsx(styles.animatedStep, currentStep === 'brand' && styles.fullWidthStep),
                            children: [
                                ((config === null || config === void 0 ? void 0 : config.title) || (config === null || config === void 0 ? void 0 : config.subtitle)) && /*#__PURE__*/ _jsxs("div", {
                                    className: styles.titleBlock,
                                    children: [
                                        (config === null || config === void 0 ? void 0 : config.title) && /*#__PURE__*/ _jsx(Typography, {
                                            variant: "h2",
                                            weight: "bold",
                                            className: styles.title,
                                            children: config.title
                                        }),
                                        (config === null || config === void 0 ? void 0 : config.subtitle) && /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph2",
                                            className: styles.subtitle,
                                            children: config.subtitle
                                        })
                                    ]
                                }),
                                renderStepContent()
                            ]
                        }, currentStep)
                    })
                })
            ]
        })
    });
};
